import classNames from 'classnames'
import type { FC } from 'react'
import React, { useEffect, useMemo } from 'react'

import { ListContextProvider } from './list-context'
import { LIST_THEME_KEY } from './types'
import type { ListProps } from './types'
import { createUseStyledComponent, useTimeout } from '../../hooks'
import type { PrivateThemeUiProps } from '../../styles-system/create-component'
import { noop } from '../../utils/function'

const useStyledComponent = createUseStyledComponent(['ul', 'ol'], 'csg-list')

export const ListPrivate = React.forwardRef<
  HTMLUListElement,
  ListProps & PrivateThemeUiProps
>(function ListPrivate(props, ref) {
  const {
    __variants,
    __themeKey = LIST_THEME_KEY, // compound component may override, i.e. Select
    variant = 'list',
    className,
    children,
    as: component = 'ul',
    dense = false,
    disablePadding = false,
    subheader,
    onMounted = noop,
    ...rest
  } = props
  const Component = useStyledComponent(component, __variants)

  const delayExecute = useTimeout()

  useEffect(() => delayExecute(onMounted), [onMounted, delayExecute])
  const baseStyles = useMemo(
    () => ({
      margin: 0,
      padding: 0,
      position: 'relative',
      ...(!disablePadding ? { py: 'sm' } : {}),
      ...(subheader ? { pt: 0 } : {})
    }),
    [disablePadding, subheader]
  )

  return (
    <ListContextProvider
      dense={dense}
      variant={variant === 'list' ? undefined : variant}
    >
      <Component
        variant={
          __themeKey === LIST_THEME_KEY // independent list component i.e. not part of other compound component
            ? variant === 'list'
              ? 'list'
              : `${variant}.list`
            : variant
        }
        __themeKey={__themeKey}
        className={classNames(
          {
            'csg-tui-list': true
          },
          className
        )}
        __css={baseStyles}
        ref={ref}
        {...rest}
      >
        {subheader}
        {typeof children === 'function' ? children() : children}
      </Component>
    </ListContextProvider>
  )
})

const MemoList = React.memo(ListPrivate)

const List: FC<ListProps> = React.forwardRef<HTMLUListElement, ListProps>(
  function List(props, ref) {
    return <MemoList ref={ref} {...props} />
  }
)

export default List
